/**
 * Progress dialog component for clone table operations.
 * Shows real-time progress, allows cancellation, and displays success/error states.
 */

import { useEffect, useState, useRef, useCallback } from 'react';
import { CheckCircle2, XCircle, AlertCircle, Copy, ChevronDown, ChevronUp, Clipboard, ExternalLink, Table2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { Progress } from '../../../components/ui/progress';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../../components/ui/alert-dialog';
import { toast } from '../../../components/ui/toaster';
import type { CloneTableProgressEvent, CloneTableResult } from '../types/cloneTable';
import { useCloneTableUiStore } from '../store/cloneTableUiStore';

import { RandomDotmSpinner } from '@/components/ui/RandomDotmSpinner';
import { writeClipboardText } from '@/lib/clipboard';
type CloneStatus = 'in-progress' | 'succeeded' | 'failed' | 'cancelled';

type CloneTableProgressDialogProps = {
  open: boolean;
  onClose: () => void;
  operationId: string;
  sourceTableName: string;
  targetTableName: string;
  /**
   * Fired once per operation as soon as the target table exists on the server.
   * The Object Explorer uses it to drop its cached table listing and reload the
   * target's Tables folder, so the table shows up without the user pressing
   * Refresh. Note this covers a *failed* clone too: the structure DDL commits
   * before the data copy, so a copy failure leaves a real table behind that the
   * user is told to go drop — it has to be visible in the tree.
   */
  onTargetTableChanged?: () => void;
  onOpenInExplorer?: () => void;
  onOpenInTableExplorer?: () => void;
};

function formatElapsedTime(ms: number): string {
  const seconds = Math.floor(ms / 1000);
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;

  if (minutes > 0) {
    return `${minutes}m ${remainingSeconds}s`;
  }
  return `${seconds}s`;
}

export function CloneTableProgressDialog({
  open,
  onClose,
  operationId,
  sourceTableName,
  targetTableName,
  onTargetTableChanged,
  onOpenInExplorer,
  onOpenInTableExplorer,
}: CloneTableProgressDialogProps) {
  const [status, setStatus] = useState<CloneStatus>('in-progress');
  const [progress, setProgress] = useState<number>(0);
  const [message, setMessage] = useState<string>('Preparing...');
  const [currentItem, setCurrentItem] = useState<string | undefined>();
  const [error, setError] = useState<string | undefined>();
  const [result, setResult] = useState<CloneTableResult | undefined>();
  const [showErrorDetails, setShowErrorDetails] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [rowsCopied, setRowsCopied] = useState<number | undefined>();
  const [totalRows, setTotalRows] = useState<number | undefined>();
  const [partialTargetTable, setPartialTargetTable] = useState<string | undefined>();
  const startTimeRef = useRef<number>(Date.now());
  const [elapsedTime, setElapsedTime] = useState<number>(0);

  // A terminal phase can reach us twice — once replayed from the store seed, once
  // from the live listener — and the subscribe effect re-runs on prop changes.
  // Latch per operation so the Object Explorer refresh fires exactly once.
  const targetChangeNotifiedRef = useRef<string | null>(null);
  // Held in a ref so a new callback identity doesn't tear down the subscription.
  const onTargetTableChangedRef = useRef(onTargetTableChanged);
  onTargetTableChangedRef.current = onTargetTableChanged;

  // Update elapsed time every second
  useEffect(() => {
    if (status !== 'in-progress') return;

    const interval = setInterval(() => {
      setElapsedTime(Date.now() - startTimeRef.current);
    }, 1000);

    return () => clearInterval(interval);
  }, [status]);

  // Subscribe to progress events. Also seed from the store synchronously so
  // events that arrived before this useEffect runs (race between main emitting
  // events during `clone-table-start` IPC and renderer mounting) are not lost.
  // The module-level bridge (`initCloneTableProgressBridge`) subscribes at app
  // startup and routes every event into the store, so we can replay them here.
  useEffect(() => {
    if (!operationId || !window.electronAPI?.cloneTable) return;

    const notifyTargetChangedOnce = (): void => {
      if (targetChangeNotifiedRef.current === operationId) return;
      targetChangeNotifiedRef.current = operationId;
      onTargetTableChangedRef.current?.();
    };

    const applyEvent = (event: CloneTableProgressEvent): void => {
      if (event.operationId !== operationId) return;

      setMessage(event.message);
      setCurrentItem(event.currentItem);

      if (event.progress !== undefined) {
        setProgress(event.progress);
      }

      if (event.rowsCopied !== undefined) {
        setRowsCopied(event.rowsCopied);
      }
      if (event.totalRows !== undefined) {
        setTotalRows(event.totalRows);
      }

      switch (event.phase) {
        case 'complete':
          setStatus('succeeded');
          setResult({
            success: true,
            targetTableName,
            rowsCopied: event.rowsCopied,
            elapsedMs: Date.now() - startTimeRef.current,
          });
          notifyTargetChangedOnce();
          break;
        case 'failed':
          setStatus('failed');
          setError(event.error || event.message);
          setPartialTargetTable(event.partialTargetTable);
          // A failed data copy still leaves the created table on the server, and
          // the banner tells the user to go drop it — so the tree has to show it.
          // Refreshing only on success would name a table the explorer doesn't list.
          if (event.partialTargetTable) notifyTargetChangedOnce();
          break;
        case 'cancelled':
          setStatus('cancelled');
          break;
        default:
          setStatus('in-progress');
      }
    };

    // Seed from store (captures events emitted before subscription)
    const seeded = useCloneTableUiStore.getState().operations.find(
      (op) => op.operationId === operationId
    );
    if (seeded) {
      if (seeded.message) setMessage(seeded.message);
      if (seeded.progress !== undefined) setProgress(seeded.progress);
      if (seeded.rowsCopied !== undefined) setRowsCopied(seeded.rowsCopied);
      if (seeded.totalRows !== undefined) setTotalRows(seeded.totalRows);
      if (seeded.status === 'succeeded') {
        setStatus('succeeded');
        setResult({
          success: true,
          targetTableName,
          rowsCopied: seeded.rowsCopied,
          elapsedMs: Date.now() - startTimeRef.current,
        });
        notifyTargetChangedOnce();
      } else if (seeded.status === 'failed') {
        setStatus('failed');
        setError(seeded.error || seeded.message);
        setPartialTargetTable(seeded.partialTargetTable);
        if (seeded.partialTargetTable) notifyTargetChangedOnce();
      } else if (seeded.status === 'cancelled') {
        setStatus('cancelled');
      }
    }

    const unsubscribe = window.electronAPI.cloneTable.onProgress(applyEvent);

    return () => {
      unsubscribe();
    };
  }, [operationId, targetTableName]);

  const handleCancel = useCallback(async () => {
    if (!window.electronAPI?.cloneTable) return;

    setIsCancelling(true);
    try {
      const response = await window.electronAPI.cloneTable.cancel({ operationId });
      if (!response.success) {
        toast.error({
          title: 'Cancel Failed',
          description: response.error || 'Could not cancel operation',
        });
      }
    } catch (err) {
      toast.error({
        title: 'Cancel Failed',
        description: err instanceof Error ? err.message : 'Unknown error',
      });
    } finally {
      setIsCancelling(false);
      setShowCancelConfirm(false);
    }
  }, [operationId]);

  const handleCopyError = useCallback(() => {
    if (error) {
      void writeClipboardText(error);
      toast.success({ title: 'Error copied to clipboard' });
    }
  }, [error]);

  const handleClose = useCallback(() => {
    if (status === 'in-progress') {
      setShowCancelConfirm(true);
    } else {
      onClose();
    }
  }, [status, onClose]);

  const getStatusIcon = () => {
    switch (status) {
      case 'succeeded':
        return <CheckCircle2 className="h-5 w-5 text-green-500" />;
      case 'failed':
        return <XCircle className="h-5 w-5 text-destructive" />;
      case 'cancelled':
        return <AlertCircle className="h-5 w-5 text-muted-foreground" />;
      default:
        return <RandomDotmSpinner size={20} className="text-primary" />;
    }
  };

  const getStatusLabel = () => {
    switch (status) {
      case 'succeeded':
        return 'Clone Complete';
      case 'failed':
        return 'Clone Failed';
      case 'cancelled':
        return 'Clone Cancelled';
      default:
        return 'Cloning Table...';
    }
  };

  const getProgressDisplay = () => {
    if (rowsCopied !== undefined && totalRows !== undefined && totalRows > 0) {
      return `${rowsCopied.toLocaleString()} / ${totalRows.toLocaleString()} rows`;
    }
    return `${Math.round(progress)}%`;
  };

  return (
    <>
      <Dialog open={open} onOpenChange={(isOpen) => !isOpen && handleClose()}>
        <DialogContent
          data-testid="clone-table-progress-dialog"
          className="sm:max-w-[500px]"
        >
          <DialogHeader>
            <div className="flex items-center gap-3">
              <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary/10 text-primary">
                <Copy className="h-5 w-5" />
              </div>
              <div className="flex-1">
                <DialogTitle>{getStatusLabel()}</DialogTitle>
                <DialogDescription className="mt-1">
                  {sourceTableName} → {targetTableName}
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>

          <div className="py-4 space-y-4">
            {/* Status header */}
            <div className="flex items-center gap-2">
              {getStatusIcon()}
              <span className="font-medium">{message}</span>
              {status === 'in-progress' && (
                <span className="text-sm text-muted-foreground ml-auto tabular-nums">
                  {getProgressDisplay()}
                </span>
              )}
            </div>

            {/* Progress bar */}
            {status === 'in-progress' && (
              <Progress
                value={progress}
                className="h-2"
                data-testid="clone-table-progress-bar"
              />
            )}

            {/* Current item */}
            {currentItem && status === 'in-progress' && (
              <p
                className="text-sm text-muted-foreground"
                data-testid="clone-table-progress-current-item"
              >
                {currentItem}
              </p>
            )}

            {/* Elapsed time */}
            <div className="flex items-center justify-between text-sm text-muted-foreground">
              <span>Elapsed time:</span>
              <span data-testid="clone-table-elapsed-time" className="tabular-nums">
                {formatElapsedTime(
                  status === 'in-progress' ? elapsedTime : (result?.elapsedMs || elapsedTime)
                )}
              </span>
            </div>

            {/* Success summary */}
            {status === 'succeeded' && result && (
              <div
                className="p-3 rounded-md bg-green-500/10 border border-green-500/30"
                data-testid="clone-table-success-banner"
              >
                <p className="text-sm text-green-700 dark:text-green-400">
                  Table cloned successfully.
                  {result.constraintsCreated !== undefined && result.constraintsCreated > 0 && (
                    <> {result.constraintsCreated} constraints</>
                  )}
                  {result.indexesCreated !== undefined && result.indexesCreated > 0 && (
                    <>, {result.indexesCreated} indexes</>
                  )}
                  {result.rowsCopied !== undefined && result.rowsCopied > 0 && (
                    <>, {result.rowsCopied.toLocaleString()} rows</>
                  )}
                  {' copied.'}
                </p>
              </div>
            )}

            {/* Left-behind target table. Structure DDL commits before the data
                copy starts, so a data-copy failure still leaves a table on the
                server — say so instead of letting "Clone Failed" contradict the
                table the user can see in the Object Explorer. */}
            {status === 'failed' && partialTargetTable && (
              <div
                className="flex items-start gap-2 p-3 rounded-md bg-amber-500/10 border border-amber-500/30 text-amber-700 dark:text-amber-400"
                data-testid="clone-table-partial-target-banner"
              >
                <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                <p className="text-sm">
                  <span className="font-medium">{partialTargetTable}</span> was created before
                  the copy failed
                  {rowsCopied !== undefined && rowsCopied > 0
                    ? ` and holds ${rowsCopied.toLocaleString()} partially copied ${
                        rowsCopied === 1 ? 'row' : 'rows'
                      }`
                    : ' and is empty'}
                  . Drop it before retrying the clone.
                </p>
              </div>
            )}

            {/* Error display */}
            {status === 'failed' && error && (
              <div className="space-y-2">
                <div className="p-3 rounded-md bg-destructive/10 border border-destructive/30">
                  <p
                    className="text-sm text-destructive"
                    data-testid="clone-table-error-message"
                  >
                    {error.length > 200 && !showErrorDetails
                      ? `${error.substring(0, 200)}...`
                      : error}
                  </p>
                </div>

                {error.length > 200 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowErrorDetails(!showErrorDetails)}
                    className="text-xs"
                  >
                    {showErrorDetails ? (
                      <>
                        <ChevronUp className="h-3 w-3 mr-1" />
                        Show less
                      </>
                    ) : (
                      <>
                        <ChevronDown className="h-3 w-3 mr-1" />
                        View details
                      </>
                    )}
                  </Button>
                )}

                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleCopyError}
                  className="text-xs"
                >
                  <Clipboard className="h-3 w-3 mr-1" />
                  Copy error
                </Button>
              </div>
            )}

            {/* Cancelled message */}
            {status === 'cancelled' && (
              <div className="p-3 rounded-md bg-muted border border-border">
                <p className="text-sm text-muted-foreground">
                  The clone operation was cancelled. Partial data may remain if data copying was in progress.
                </p>
              </div>
            )}
          </div>

          <DialogFooter className="flex items-center gap-2">
            {status === 'in-progress' ? (
              <Button
                variant="outline"
                onClick={() => setShowCancelConfirm(true)}
                disabled={isCancelling}
                data-testid="clone-table-cancel-button"
              >
                {isCancelling ? 'Cancelling...' : 'Cancel'}
              </Button>
            ) : (
              <>
                {status === 'succeeded' && onOpenInTableExplorer && (
                  <Button
                    variant="outline"
                    onClick={onOpenInTableExplorer}
                    data-testid="clone-table-open-table-explorer-button"
                  >
                    <Table2 className="h-4 w-4 mr-2" />
                    Open in Table Explorer
                  </Button>
                )}
                {status === 'succeeded' && onOpenInExplorer && (
                  <Button
                    variant="outline"
                    onClick={onOpenInExplorer}
                    data-testid="clone-table-open-explorer-button"
                  >
                    <ExternalLink className="h-4 w-4 mr-2" />
                    Open in Explorer
                  </Button>
                )}
                <Button
                  onClick={onClose}
                  data-testid="clone-table-close-button"
                >
                  {status === 'succeeded' ? 'Done' : 'Close'}
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel confirmation dialog */}
      <AlertDialog open={showCancelConfirm} onOpenChange={setShowCancelConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel Clone Operation?</AlertDialogTitle>
            <AlertDialogDescription>
              The clone operation is still in progress. Cancelling may leave partial data
              if data copying was in progress.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="clone-table-cancel-confirm-no">
              Continue Cloning
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleCancel}
              className="bg-destructive-fill text-destructive-foreground hover:bg-destructive-fill/90"
              data-testid="clone-table-cancel-confirm-yes"
            >
              Cancel Clone
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
